import { useState, useEffect } from "react";
import { teamMembers, BURGUNDY } from "./tdata";

const LinkedInIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const TwitterIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
    <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
  </svg>
);

function SocialLink({ href, label, hovered, children }) {
  if (!href) return null;
  return (
    <a
      href={href}
      aria-label={label}
      className={`flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center border text-black no-underline transition-all duration-200 ${
        hovered ? "text-white" : "border-black/25"
      }`}
      style={{
        borderColor: hovered ? BURGUNDY : undefined,
        backgroundColor: hovered ? BURGUNDY : "transparent",
      }}
    >
      {children}
    </a>
  );
}

function MemberCard({ member, onOpen }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onOpen(member)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" ? onOpen(member) : null)}
      className="relative flex h-full cursor-pointer flex-col overflow-hidden border border-black bg-white transition-transform duration-300"
      style={{
        transform: hovered ? "translateY(-6px)" : "translateY(0)",
        boxShadow: hovered
          ? "0 16px 40px rgba(0,0,0,0.18)"
          : "0 2px 8px rgba(0,0,0,0.06)",
      }}
    >
      {/* Burgundy top accent bar */}
      <div
        className="absolute left-0 right-0 top-0 z-10 h-1 origin-left transition-transform duration-300"
        style={{
          backgroundColor: BURGUNDY,
          transform: hovered ? "scaleX(1)" : "scaleX(0)",
        }}
      />

      {/* Image */}
      <div className="relative aspect-square w-full flex-shrink-0 overflow-hidden bg-[#ece8e2]">
        <img
          src={member.image}
          alt={member.name}
          className="block h-full w-full object-cover object-center transition-transform duration-500"
          style={{ transform: hovered ? "scale(1.04)" : "scale(1)" }}
        />
        <div
          className="pointer-events-none absolute bottom-0 left-0 right-0 h-12"
          style={{ background: "linear-gradient(to bottom, transparent, #ece8e2)" }}
        />
      </div>

      {/* Content area */}
      <div className="flex flex-grow flex-col gap-2 bg-white px-[1.1rem] pb-[1.1rem] pt-[0.9rem]">
        <div>
          <div className="font-serif text-[clamp(0.85rem,1.2vw,1rem)] font-bold leading-tight tracking-tight text-black">
            {member.name}
          </div>
          <div
            className="mt-1 font-mono text-[clamp(0.6rem,0.9vw,0.7rem)] uppercase tracking-[0.12em]"
            style={{ color: BURGUNDY }}
          >
            {member.role}
          </div>
        </div>

        <div
          className="h-px transition-colors duration-300"
          style={{ backgroundColor: hovered ? BURGUNDY : "rgba(0,0,0,0.12)" }}
        />

        <p className="m-0 flex-grow font-serif text-[clamp(0.72rem,0.9vw,0.8rem)] leading-relaxed text-[#333]">
          {member.bio}
        </p>

        <div className="flex gap-2 pt-1" onClick={(e) => e.stopPropagation()}>
          <SocialLink href={member.social.linkedin} label={`${member.name} on LinkedIn`} hovered={hovered}>
            <LinkedInIcon />
          </SocialLink>
          <SocialLink href={member.social.twitter} label={`${member.name} on Twitter`} hovered={hovered}>
            <TwitterIcon />
          </SocialLink>
        </div>

        <div
          className="mt-1 font-mono text-[0.65rem] uppercase tracking-[0.1em] text-black/40 transition-opacity"
          style={{ opacity: hovered ? 1 : 0.55 }}
        >
          View full profile →
        </div>
      </div>
    </div>
  );
}

function MemberModal({ member, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  if (!member) return null;
  const d = member.details;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 sm:items-center sm:p-8"
      onClick={onClose}
    >
      <div
        className="relative my-6 w-full max-w-3xl border border-black bg-white sm:my-0"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center border border-black/20 bg-white text-black transition-colors hover:border-black"
        >
          <CloseIcon />
        </button>

        <div
          className="h-1 w-full"
          style={{ backgroundColor: BURGUNDY }}
        />

        <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr]">
          {/* Photo column */}
          <div className="bg-[#ece8e2] p-6 sm:p-5">
            <div className="aspect-square w-full overflow-hidden border border-black/10 sm:sticky sm:top-5">
              <img
                src={member.image}
                alt={member.name}
                className="h-full w-full object-cover object-center"
              />
            </div>
            <div className="mt-4 flex gap-2">
              <SocialLink href={member.social.linkedin} label={`${member.name} on LinkedIn`} hovered={false}>
                <LinkedInIcon />
              </SocialLink>
              <SocialLink href={member.social.twitter} label={`${member.name} on Twitter`} hovered={false}>
                <TwitterIcon />
              </SocialLink>
            </div>
          </div>

          {/* Details column */}
          <div className="max-h-[80vh] overflow-y-auto px-6 py-6 sm:px-8 sm:py-8">
            <div
              className="font-mono text-[0.7rem] uppercase tracking-[0.2em]"
              style={{ color: BURGUNDY }}
            >
              — Profile
            </div>
            <h2 className="mt-3 font-serif text-2xl font-bold leading-tight text-black sm:text-3xl">
              {member.name}
            </h2>
            <p className="mt-1 font-mono text-xs uppercase tracking-[0.1em] text-black/60">
              {member.role}
            </p>

            <div className="mt-5 h-px bg-black/10" />

            <p className="mt-5 font-serif text-[0.95rem] leading-relaxed text-[#333]">
              {d?.intro || member.bio}
            </p>

            {d?.corePractice && (
              <div className="mt-5 flex flex-wrap gap-2">
                {d.corePractice.map((item) => (
                  <span
                    key={item}
                    className="border px-2.5 py-1 font-mono text-[0.65rem] uppercase tracking-wide text-black/70"
                    style={{ borderColor: "rgba(0,0,0,0.15)" }}
                  >
                    {item}
                  </span>
                ))}
              </div>
            )}

            {d?.practiceAreas && (
              <section className="mt-8">
                <h3
                  className="font-mono text-xs uppercase tracking-[0.15em]"
                  style={{ color: BURGUNDY }}
                >
                  Practice Areas
                </h3>
                <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {d.practiceAreas.map((area) => (
                    <div key={area.title}>
                      <div className="font-serif text-sm font-bold text-black">
                        {area.title}
                      </div>
                      <p className="mt-1 font-serif text-[0.82rem] leading-relaxed text-[#444]">
                        {area.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {d?.courts && (
              <section className="mt-8">
                <h3
                  className="font-mono text-xs uppercase tracking-[0.15em]"
                  style={{ color: BURGUNDY }}
                >
                  Courts & Forums
                </h3>
                <ul className="mt-3 grid list-none grid-cols-1 gap-x-6 gap-y-1.5 p-0 sm:grid-cols-2">
                  {d.courts.map((c) => (
                    <li
                      key={c}
                      className="border-l-2 pl-3 font-serif text-[0.85rem] text-[#333]"
                      style={{ borderColor: "rgba(0,0,0,0.15)" }}
                    >
                      {c}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {d?.approach && (
              <section className="mt-8">
                <h3
                  className="font-mono text-xs uppercase tracking-[0.15em]"
                  style={{ color: BURGUNDY }}
                >
                  Professional Approach
                </h3>
                <p className="mt-3 font-serif text-[0.85rem] leading-relaxed text-[#333]">
                  {d.approach}
                </p>
              </section>
            )}

            {d?.experience && (
              <section className="mt-8">
                <h3
                  className="font-mono text-xs uppercase tracking-[0.15em]"
                  style={{ color: BURGUNDY }}
                >
                  Experience — {d.experience.period}
                </h3>
                <p className="mt-3 font-serif text-[0.85rem] leading-relaxed text-[#333]">
                  {d.experience.text}
                </p>
              </section>
            )}

            {d?.education && (
              <section className="mb-2 mt-8">
                <h3
                  className="font-mono text-xs uppercase tracking-[0.15em]"
                  style={{ color: BURGUNDY }}
                >
                  Education
                </h3>
                <div className="mt-3 space-y-2">
                  {d.education.map((e) => (
                    <div key={e.degree} className="font-serif text-[0.85rem] text-[#333]">
                      <span className="font-bold text-black">{e.degree}</span>
                      {e.institution && <> — {e.institution}</>}
                      {e.year && <span className="text-black/50"> ({e.year})</span>}
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Team() {
  const [selected, setSelected] = useState(null);

  return (
    <div className="bg-white font-serif text-black">
      {/* Hero */}
      <section className="max-w-[960px] border-b border-black px-5 pb-10 pt-14 sm:px-10 sm:pb-10 sm:pt-14">
        <div
          className="mb-5 font-mono text-[0.7rem] uppercase tracking-[0.2em]"
          style={{ color: BURGUNDY }}
        >
          — The Counsel
        </div>
        <h1 className="m-0 font-serif text-[clamp(1.6rem,4.5vw,3.2rem)] font-bold leading-[1.1] tracking-tight text-black">
          A compact firm with deep expertise and bold convictions.
        </h1>
        <p className="mb-0 mt-6 max-w-[560px] font-serif text-[clamp(0.95rem,2vw,1.1rem)] leading-relaxed text-[#333]">
          Seven advocates who have each spent a career sharpening their practice — united to fight for outcomes that genuinely matter.
        </p>
      </section>

      {/* Responsive Grid */}
      <main className="grid grid-cols-1 gap-4 border-b border-black bg-white p-4 sm:grid-cols-2 sm:gap-5 sm:p-5 lg:grid-cols-3">
        {teamMembers.map((member) => (
          <MemberCard key={member.id} member={member} onOpen={setSelected} />
        ))}
      </main>

      {selected && (
        <MemberModal member={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}